/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// Services
import currencyService from "services/currencyService";

// Icons
import { IoWallet, IoTrendingUp } from "react-icons/io5";

function BalanceOverview({ balance, loading, mythPrice }) {
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
    // Load currency preference on mount
    setCurrency(currencyService.getCurrentCurrency());
    
    // Listen for currency changes (check every 5 seconds instead of 1)
    const interval = setInterval(() => {
      const newCurrency = currencyService.getCurrentCurrency();
      if (newCurrency !== currency) {
        setCurrency(newCurrency);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currency]);

  // Calculate fiat value
  const fiatValue = balance * (mythPrice || 0);
  const formattedFiat = currencyService.formatAmount(fiatValue);
  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox display="flex" alignItems="center" mb={3}>
          <VuiBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="48px"
            height="48px"
            borderRadius="12px"
            sx={{
              background: "#00CED1",
            }}
          >
            <IoWallet size="24px" color="#000000" />
          </VuiBox>
          <VuiBox ml={2}>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Balance Overview
            </VuiTypography>
            <VuiTypography variant="button" color="text">
              Your wallet balance
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* Total Balance */}
        <VuiBox mb={3}>
          <VuiBox
            p={3}
            sx={{
              background: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%)",
              borderRadius: "12px",
              border: "1px solid transparent",
              backgroundImage: "linear-gradient(135deg, rgba(0, 117, 121, 0.2) 0%, rgba(75, 23, 126, 0.2) 100%), linear-gradient(135deg, rgba(0, 206, 209, 0.6) 0%, rgba(138, 43, 226, 0.6) 100%)",
              backgroundOrigin: "border-box",
              backgroundClip: "padding-box, border-box",
            }}
          >
            <VuiBox display="flex" alignItems="center" mb={1}>
              <Icon sx={{ color: "#FFD700", fontSize: "20px", mr: 1 }}>
                account_balance_wallet
              </Icon>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                Total Balance
              </VuiTypography>
            </VuiBox>
            <VuiTypography variant="h2" color="white" fontWeight="bold">
              {loading ? "..." : balance.toFixed(8)}
            </VuiTypography>
            <VuiBox display="flex" alignItems="center" gap={2}>
              <VuiTypography variant="button" color="text">
                MYT
              </VuiTypography>
              <VuiTypography variant="button" color="success" fontWeight="medium">
                ≈ {loading ? "..." : formattedFiat}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {/* Available Balance */}
        <VuiBox mb={3}>
          <VuiBox
            p={2}
            sx={{
              background: "rgba(0, 206, 209, 0.1)",
              borderRadius: "8px",
              border: "1px solid rgba(0, 206, 209, 0.2)",
            }}
          >
            <VuiBox display="flex" justifyContent="space-between" alignItems="center">
              <VuiBox display="flex" alignItems="center">
                <Icon sx={{ color: "#00CED1", fontSize: "18px", mr: 1 }}>
                  check_circle
                </Icon>
                <VuiTypography variant="button" color="text">
                  Available
                </VuiTypography>
              </VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="bold">
                {loading ? "..." : balance.toFixed(8)} MYT
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {/* Pending Balance */}
        <VuiBox mb={3}>
          <VuiBox
            p={2}
            sx={{
              background: "rgba(255, 215, 0, 0.1)",
              borderRadius: "8px",
              border: "1px solid rgba(255, 215, 0, 0.2)",
            }}
          >
            <VuiBox display="flex" justifyContent="space-between" alignItems="center">
              <VuiBox display="flex" alignItems="center">
                <Icon sx={{ color: "#FFD700", fontSize: "18px", mr: 1 }}>
                  pending
                </Icon>
                <VuiTypography variant="button" color="text">
                  Pending
                </VuiTypography>
              </VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="bold">
                0.00000000 MYT
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {/* Info Box */}
        <VuiBox p={2} sx={{ background: "rgba(0, 117, 255, 0.1)", borderRadius: "8px" }}>
          <VuiBox display="flex" alignItems="flex-start">
            <IoTrendingUp size="18px" color="#0075F6" style={{ marginRight: "8px", marginTop: "2px" }} />
            <VuiBox>
              <VuiTypography variant="caption" color="info" fontWeight="medium">
                Balance Updates
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                Your balance updates automatically every 30 seconds
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default BalanceOverview;
